import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import katex from 'katex';

@Component({
	selector: 'app-math-latex',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './math-latex.component.html',
	styleUrl: './math-latex.component.scss'
})
export class MathLatexComponent implements OnChanges {

	@Input() content: any = '';
	@Input() displayMode: boolean = false;
	/** Diagram objects from question.diagrams[]. Each entry must have a `url` field.
	 *  [DIAGRAM_N] in content is replaced with an inline <img> pointing to diagrams[N].url. */
	@Input() diagrams: any[] = [];

	renderedContent: SafeHtml = '';

	constructor(private sanitizer: DomSanitizer) { }

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['content'] || changes['diagrams']) {
			this.renderMath();
		}
	}

	isMathText(content: string): boolean {
		if (!content) return false;

		return /\\[a-zA-Z]+/.test(content)
			|| /\$.*?\$/.test(content)
			|| /\^/.test(content)
			|| /_/.test(content)
			|| /\\frac/.test(content)
			|| /\\[,;&!]/.test(content)
			|| /\\\\/.test(content);
	}

	private renderMath(): void {
		if (!this.content) {
			this.renderedContent = '';
			return;
		}

		try {

			let text = String(this.content)
				.replace(/\\\\/g, '\\')
				.replace(/\\n/g, ' ')
				.trim();

			text = text
				.replace(/[\u0008]/g, '\\')
				.replace(/[\u000C]/g, '\\');

			text = text
				.replace(/\\egin\{/g, '\\begin{')
				.replace(/\\rac\{/g, '\\frac{')
				.replace(/\\nd\{/g, '\\end{');

			text = text.replace(/\\\(x\)/g, 'f(x)');

			text = text.replace(
				/\$(\s*\\frac\{[^}]*)\$\s*([^}]*\}[^}]*\})/g,
				'$$$1 $2$'
			);

			text = text.replace(
				/\$(\s*\\[a-zA-Z]+\{(?:[^{}]|\{[^{}]*\})*)\$([^$]*?\}[^$]*?\})/g,
				'$$$1$2$'
			);

			text = text.replace(/H\$_\{(\d)\}\$\$_\{(\d)\}\$/g, 'H$_{$1$2}$');
			text = this.substituteDiagrams(text);
			text = this.fixMalformedDollarDelimiters(text);

			if (/\\begin\{cases\}/.test(text)) {
				text = text.replace(
					/([^$]*?)(\\begin\{cases\}[\s\S]*?\\end\{cases\})/g,
					(match, prefix, casesBlock) => {
						const trimmedPrefix = prefix.trimEnd();
						const mathPrefix = /[=fx()\s\\]+$/.test(trimmedPrefix)
							? trimmedPrefix.replace(/([^$]*)$/, '$1')
							: '';
						const textPrefix = trimmedPrefix.slice(0, trimmedPrefix.length - mathPrefix.length);
						return textPrefix + `$${mathPrefix}${casesBlock}$`;
					}
				);
			} else {
				text = this.normalizeToDelimitedLatex(text);
			}

			if (!text.includes('$')) {
				this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(text);
				return;
			}

			this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(
				this.renderLatexString(text)
			);
		} catch (error) {
			this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(String(this.content));
		}
	}

	private fixMalformedDollarDelimiters(text: string): string {
		let result = text;

		result = result.replace(
			/\$(\s*\\[a-zA-Z]+)([\s\S]*?)(\$)([\s\S]*?\}[\s\S]*?\})/g,
			(match, cmd, before, strayDollar, after) => {
				const beforeStray = cmd + before;
				const openBraces = (beforeStray.match(/\{/g) || []).length;
				const closeBraces = (beforeStray.match(/\}/g) || []).length;
				if (openBraces > closeBraces) {
					return `$${cmd}${before}${after}$`;
				}
				return match;
			}
		);

		return result;
	}

	private normalizeToDelimitedLatex(text: string): string {
		if (!text.includes('$') && !/\\[a-zA-Z;,!{]/.test(text)) return text;

		if (!text.includes('$')) return `$${text}$`;

		const tokens = text.split(/(\$\$[\s\S]*?\$\$|\$[^$\n]*?\$)/g);

		let insideLen = 0;
		tokens.forEach((t, i) => { if (i % 2 !== 0) insideLen += t.length; });
		const outsideRatio = 1 - insideLen / text.length;

		if (outsideRatio > 0.7) {
			return text;
		}
		return tokens.map((token, idx) => {
			if (idx % 2 !== 0) return token;
			if (!/\\[a-zA-Z;,!{]/.test(token)) return token;

			let result = '';
			let i = 0;
			while (i < token.length) {
				if (token[i] === '\\' && i + 1 < token.length && /[a-zA-Z{;,!]/.test(token[i + 1])) {
					let run = '';
					while (i < token.length) {
						if (token[i] === '\\' && /[a-zA-Z{;,!]/.test(token[i + 1] ?? '')) {
							run += token[i++];
							while (i < token.length && /[a-zA-Z]/.test(token[i])) run += token[i++];
							while (i < token.length && (token[i] === '{' || token[i] === '[')) {
								const close = token[i] === '{' ? '}' : ']';
								run += token[i++];
								while (i < token.length && token[i] !== close) run += token[i++];
								if (i < token.length) run += token[i++];
							}
						} else if (token[i] === ' ' || token[i] === '~') {
							const rest = token.slice(i + 1);
							if (/^\\[a-zA-Z{;,!]/.test(rest)) {
								run += token[i++];
							} else {
								break;
							}
						} else if (/[_^]/.test(token[i])) {
							run += token[i++];
							if (i < token.length && token[i] === '{') {
								run += token[i++];
								while (i < token.length && token[i] !== '}') run += token[i++];
								if (i < token.length) run += token[i++];
							} else {
								while (i < token.length && /[a-zA-Z0-9]/.test(token[i])) run += token[i++];
							}
						} else {
							break;
						}
					}
					result += `$${run.trim()}$`;
				} else {
					result += token[i++];
				}
			}
			return result;
		}).join('');
	}

	private wrapLatexRuns(segment: string): string {
		if (!segment || !/\\[a-zA-Z{]/.test(segment)) return segment;

		const result: string[] = [];
		let current = '';
		let i = 0;

		while (i < segment.length) {
			if (segment[i] === '\\' && i + 1 < segment.length && /[a-zA-Z{]/.test(segment[i + 1])) {

				if (current && current.trim()) {
					result.push(current);
					current = '';
				} else if (current) {
					result.push(current);
					current = '';
				}

				let latexRun = '';
				while (i < segment.length) {
					latexRun += segment[i];
					i++;
					if (
						i < segment.length &&
						/[.!?]/.test(segment[i - 1]) &&
						i < segment.length &&
						segment[i] === ' ' &&
						i + 1 < segment.length &&
						/[A-Z]/.test(segment[i + 1]) &&
						!/\\[a-zA-Z]/.test(segment.slice(i, i + 10))
					) {
						break;
					}
				}
				result.push(`$${latexRun.trim()}$`);
			} else {
				current += segment[i];
				i++;
			}
		}
		if (current) result.push(current);

		return result.join('');
	}


	/** Replace [DIAGRAM_N] and legacy [DIAGRAM] placeholders with inline <img> tags. */
	private substituteDiagrams(text: string): string {
		// Indexed: [DIAGRAM_0], [DIAGRAM_1], …
		let result = text.replace(/\[DIAGRAM_(\d+)\]/gi, (_, idx) => {
			const i = parseInt(idx, 10);
			const url = this.diagrams?.[i]?.url || this.diagrams?.[i]?.s3_key || '';
			const alt = this.diagrams?.[i]?.alt_text || `diagram ${i}`;
			return url
				? `<img src="${this.escapeAttr(url)}" alt="${this.escapeAttr(alt)}" class="inline-question-diagram" />`
				: '';
		});

		// Legacy non-indexed [DIAGRAM] — uses diagrams[0] for backward compatibility
		result = result.replace(/\[DIAGRAM\]/gi, () => {
			const url = this.diagrams?.[0]?.url || this.diagrams?.[0]?.s3_key || '';
			return url
				? `<img src="${this.escapeAttr(url)}" alt="diagram" class="inline-question-diagram" />`
				: '';
		});

		return result;
	}

	/** Render a string that may contain $...$ / $$...$$ math zones via KaTeX. */
	private renderLatexString(text: string): string {
		const parts = text.split(/(\$\$[\s\S]+?\$\$|\$[^$]+\$)/g);
		return parts.map((part: string) => {
			if (part.startsWith('$$') && part.endsWith('$$')) {
				const latex = part.slice(2, -2).trim();
				return katex.renderToString(latex, {
					throwOnError: false, displayMode: true, strict: 'ignore'
				});
			}
			if (part.startsWith('$') && part.endsWith('$')) {
				const latex = part.slice(1, -1).trim();
				return katex.renderToString(latex, {
					throwOnError: false, displayMode: false, strict: 'ignore'
				});
			}
			return part
				.replace(/\\,/g, ' ')
				.replace(/\\;/g, ' ')
				.replace(/\\:/g, ' ')
				.replace(/\\!/g, '')
				.replace(/\\&/g, '&')
				.replace(/\\%/g, '%')
				.replace(/\\#/g, '#');
		}).join('');
	}

	private escapeAttr(text: string): string {
		return text.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	}
}
